import React from 'react';
import { SidebarTrigger } from '../ui/sidebar';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router';
import { routes } from '@/routing';
import { useAdminAuth } from '@/hooks/use-admin-auth';
import { normalizeAdminRole } from '@/services/admin-auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const AdminNavHeader: React.FC = () => {
  const location = useLocation();
  const { admin, logout } = useAdminAuth();
  const { t } = useTranslation();

  const roleLabel = admin?.role ? normalizeAdminRole(admin.role) : 'Admin';

  // Every admin route gets its own heading. Only Dashboard and Tenants were
  // listed before, so the other four pages all fell through to the default and
  // announced themselves as "Dashboard". Matched by prefix so detail routes
  // (/admin/tenants/:id) keep their section's heading.
  const getActiveTitle = () => {
    const sections: { path: string; key: string }[] = [
      { path: routes.adminTenants, key: 'tenants' },
      { path: routes.adminBusinesses, key: 'businesses' },
      { path: routes.adminCampaigns, key: 'campaigns' },
      { path: routes.adminReferenceData, key: 'referenceData' },
      { path: routes.adminAuditLogs, key: 'auditLogs' },
      { path: routes.adminPaymentsHealth, key: 'paymentsHealth' },
      { path: routes.adminDashboard, key: 'dashboard' },
    ];

    const match = sections.find(
      (section) =>
        location.pathname === section.path ||
        location.pathname.startsWith(`${section.path}/`)
    );

    const key = match?.key ?? 'dashboard';
    return {
      title: t(`adminNavHeader.${key}.title`),
      description: t(`adminNavHeader.${key}.description`),
    };
  };

  const activeTitle = getActiveTitle();

  return (
    <header
      className={cn(
        'flex shrink-0 items-center gap-2 border-b border-admin-border-light bg-admin-bg-card backdrop-blur supports-[backdrop-filter]:bg-admin-bg-card',
        'sticky top-0 z-40 transition-all duration-200 ease-in-out shadow-sm',
        'block'
      )}
    >
      <div className="flex items-center justify-between w-full gap-2 px-4 py-3">
        <Link
          to={routes.adminDashboard}
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
          aria-label={t('adminNavHeader.goToDashboard')}
        >
          <div className="grid flex-1 text-sm leading-tight text-left">
            <span className="text-lg font-extrabold truncate text-admin-text-dark">
              {activeTitle.title}
            </span>
            <span className="text-sm truncate text-admin-text-muted">
              {activeTitle.description}
            </span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {admin && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-admin-text-muted">
                {admin.email}
              </span>
              <Badge className="text-xs bg-slate-200 text-slate-700 hover:bg-slate-200">
                {roleLabel}
              </Badge>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-admin-text-muted hover:text-admin-text-dark"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('navigation.logout')}
          </Button>
          <SidebarTrigger
            className="w-8 h-8 transition-colors rounded-lg hover:bg-admin-bg-muted"
            aria-label={t('navHeader.sidebarToggleAriaLabel')}
          />
        </div>
      </div>
    </header>
  );
};
